import { z } from 'zod';
import { PlanCode } from './plan';
import { Platform } from './platform';

export const SkillLayer = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
]);
export type SkillLayer = z.infer<typeof SkillLayer>;

export const SkillSourceType = z.enum(['platform', 'user', 'extracted', 'composed']);
export type SkillSourceType = z.infer<typeof SkillSourceType>;

export const SkillDomain = z.enum(['platform', 'user', 'chat']);
export type SkillDomain = z.infer<typeof SkillDomain>;
export type NonChatSkillDomain = Exclude<SkillDomain, 'chat'>;

// Skill 选择契约：T2 生成与 T4 前端只能依赖这些公开字段。
// 不包含 tenant_id、任何 *_internal 字段、R2 key 或模板正文。
export type SkillSummary = {
  skill_id: string;
  skill_domain: NonChatSkillDomain;
  platform: Platform;
  layer: SkillLayer;
  name: string;
  category: string | null;
  tier_required: PlanCode;
  summary: string;
  source_type: SkillSourceType;
  usage_count: number;
  effectiveness_score: number | null;
  created_at: string;
  updated_at: string;
};

export const SkillsListQuery = z.object({
  platform: Platform,
  layer: z.coerce.number().int().pipe(SkillLayer).optional(),
  source_type: SkillSourceType.optional(),
  category: z.string().min(1).max(80).optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});
export type SkillsListQuery = z.infer<typeof SkillsListQuery>;

export const SkillSelectRequest = z
  .object({
    platform: Platform,
    skill_ids: z.array(z.string().min(1).max(128)).min(1).max(20),
  })
  .superRefine((value, ctx) => {
    if (new Set(value.skill_ids).size === value.skill_ids.length) return;
    ctx.addIssue({
      code: 'custom',
      path: ['skill_ids'],
      message: 'skill_ids must be unique',
    });
  });
export type SkillSelectRequest = z.infer<typeof SkillSelectRequest>;

export type SkillsListResponse = {
  items: SkillSummary[];
};

export type SkillReadResponse = {
  skill: SkillSummary;
};

export type SkillSelectResponse = {
  items: SkillSummary[];
};

export const SkillExtractionReadinessQuery = z.object({
  platform: Platform,
});
export type SkillExtractionReadinessQuery = z.infer<typeof SkillExtractionReadinessQuery>;

export const SkillExtractionStartRequest = z
  .object({
    platform: Platform,
    confirmed: z.literal(true),
  })
  .strict();
export type SkillExtractionStartRequest = z.infer<typeof SkillExtractionStartRequest>;

export type SkillExtractionTaskStatus =
  | 'pending'
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'skipped';

export type SkillExtractionBlockingReason =
  | 'account_unbound'
  | 'insufficient_samples'
  | 'insufficient_points'
  | 'task_active'
  | 'feature_unavailable';

export type SkillExtractionReadinessResponse = {
  platform: Platform;
  account: {
    id: string;
    display_name: string | null;
    external_handle: string | null;
  } | null;
  samples: {
    total: number;
    body_ready: number;
    sufficient: boolean;
    minimum_body_ready: number;
    source_limit: number;
  };
  quota: {
    required_points: number | null;
    available_points: number | null;
    unlimited: boolean;
  };
  task: {
    id: string;
    status: SkillExtractionTaskStatus;
    created_at: string;
    completed_at: string | null;
  } | null;
  can_start: boolean;
  blocking_reasons: SkillExtractionBlockingReason[];
};

export type SkillExtractionStartResponse = {
  task_id: string;
  status: Extract<SkillExtractionTaskStatus, 'pending' | 'queued' | 'running'>;
  reused: boolean;
};

const SkillName = z.string().trim().min(1).max(80);
const SkillCategory = z.string().trim().min(1).max(80).nullable().optional();
const SkillSummaryText = z.string().trim().min(1).max(256);
const SkillInstructions = z.string().trim().min(1).max(10_000);

export const SkillCreateRequest = z
  .object({
    name: SkillName,
    category: SkillCategory,
    summary: SkillSummaryText,
    instructions: SkillInstructions,
    client_request_id: z.string().uuid(),
  })
  .strict();
export type SkillCreateRequest = z.infer<typeof SkillCreateRequest>;

export const SkillUpdateRequest = z
  .object({
    name: SkillName.optional(),
    category: SkillCategory,
    summary: SkillSummaryText.optional(),
    instructions: SkillInstructions.optional(),
  })
  .strict()
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'at least one field is required',
  });
export type SkillUpdateRequest = z.infer<typeof SkillUpdateRequest>;

export type SkillCreateResponse = {
  skill: SkillSummary;
};

export type SkillManageResponse = {
  skill: SkillSummary;
  instructions: string;
};

export type SkillUpdateResponse = {
  skill: SkillSummary;
};

export type SkillArchiveResponse = {
  archived: true;
  skill_id: string;
};

export const AdminSkillSourceType = z.enum(['platform', 'user']);
export type AdminSkillSourceType = z.infer<typeof AdminSkillSourceType>;

export const AdminSkillStatus = z.enum(['draft', 'active', 'archived']);
export type AdminSkillStatus = z.infer<typeof AdminSkillStatus>;

export const AdminSkillsListQuery = z.object({
  source_type: AdminSkillSourceType,
  limit: z.coerce.number().int().min(1).max(50).default(20),
  cursor: z.string().trim().min(1).max(400).optional(),
  q: z.string().trim().min(1).max(120).optional(),
  platform: z.string().trim().min(1).max(40).optional(),
  layer: z.coerce.number().int().min(1).max(5).optional(),
  status: AdminSkillStatus.optional(),
  tenant_id: z.string().trim().min(1).max(128).optional(),
});
export type AdminSkillsListQuery = z.infer<typeof AdminSkillsListQuery>;

export const AdminSkillWriteRequest = z
  .object({
    platform: z.string().trim().min(1).max(40),
    layer: z.coerce.number().int().pipe(SkillLayer),
    name: SkillName,
    category: SkillCategory,
    tier_required: PlanCode,
    summary: SkillSummaryText,
    template_body: z.string().trim().min(1).max(200_000),
    patterns_body: z.string().trim().max(50_000).nullable().optional(),
  })
  .strict();
export type AdminSkillWriteRequest = z.infer<typeof AdminSkillWriteRequest>;

export const AdminSkillStatusUpdateRequest = z
  .object({ status: z.enum(['draft', 'active']) })
  .strict();
export type AdminSkillStatusUpdateRequest = z.infer<typeof AdminSkillStatusUpdateRequest>;

export type AdminSkillSummary = {
  skill_id: string;
  skill_domain: NonChatSkillDomain;
  tenant: { id: string; name: string } | null;
  platform: string;
  layer: SkillLayer;
  name: string;
  category: string | null;
  tier_required: PlanCode;
  summary: string;
  status: AdminSkillStatus;
  source_type: AdminSkillSourceType;
  usage_count: number;
  effectiveness_score: number | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
};

export type AdminSkillsListResponse = {
  items: AdminSkillSummary[];
  next_cursor: string | null;
  limit: number;
};

export type AdminSkillDetailResponse =
  | { skill: AdminSkillSummary; instructions: string }
  | { skill: AdminSkillSummary; template_body: string; patterns_body: string | null };

const AdminChatSkillApplicability = z.string().trim().min(1).max(2_000);
const AdminChatSkillInstructions = z.string().trim().min(1).max(200_000);
const AdminChatDraftRevision = z.number().int().min(0);

export const AdminChatSkillsListQuery = z
  .object({
    limit: z.coerce.number().int().min(1).max(50).default(20),
    cursor: z.string().trim().min(1).max(400).optional(),
    q: z.string().trim().min(1).max(120).optional(),
    status: AdminSkillStatus.optional(),
  })
  .strict();
export type AdminChatSkillsListQuery = z.infer<typeof AdminChatSkillsListQuery>;

export const AdminChatSkillCreateRequest = z
  .object({
    name: SkillName,
    summary: SkillSummaryText,
    applicability: AdminChatSkillApplicability,
    instructions: AdminChatSkillInstructions,
    expected_draft_revision: AdminChatDraftRevision,
  })
  .strict();
export type AdminChatSkillCreateRequest = z.infer<typeof AdminChatSkillCreateRequest>;

export const AdminChatSkillUpdateRequest = z
  .object({
    name: SkillName.optional(),
    summary: SkillSummaryText.optional(),
    applicability: AdminChatSkillApplicability.optional(),
    instructions: AdminChatSkillInstructions.optional(),
    expected_draft_revision: AdminChatDraftRevision,
  })
  .strict()
  .refine(
    (value) =>
      value.name !== undefined ||
      value.summary !== undefined ||
      value.applicability !== undefined ||
      value.instructions !== undefined,
    { message: 'at least one skill field is required' },
  );
export type AdminChatSkillUpdateRequest = z.infer<typeof AdminChatSkillUpdateRequest>;

export type AdminChatSkillSummary = {
  skill_id: string;
  skill_domain: 'chat';
  name: string;
  summary: string;
  applicability: string;
  status: AdminSkillStatus;
  revision: number;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
};

export type AdminChatSkillsListResponse = {
  items: AdminChatSkillSummary[];
  next_cursor: string | null;
  limit: number;
};

export type AdminChatSkillDetailResponse = {
  skill: AdminChatSkillSummary;
  instructions: string;
};

export type AdminChatSkillArchiveResponse = {
  archived: true;
  skill_id: string;
  revision: number;
};

export type AdminSkillOptionsResponse = {
  platforms: Array<{ code: string; display_name: string; is_active: boolean }>;
};
